import { Component } from "@theme/component";
import { requestIdleCallback } from "@theme/utilities";

/**
 * <ugc-center-carousel> — a self-contained "center stage" carousel.
 *
 * The active card is centred and rendered at its real (wider) width while the side cards keep their
 * own (narrower) real width — nothing is scaled, so the cards sit edge-to-edge and there are never
 * gaps. Positioning is done with a single analytical transform on the track:
 *
 *     T = viewportWidth / 2  -  activeIndex * (sideWidth + gap)  -  activeWidth / 2
 *
 * Every non-active slide is `sideWidth`, so the start of the active slide is always
 * `activeIndex * (sideWidth + gap)` regardless of which slide is active. A transform is not a layout
 * shift, so centring stays smooth and CLS-free, and there is no measured-grid race (which is why we
 * don't use Swiper here).
 *
 * Behaviours: infinite loop (edge clones), pointer drag/swipe, click-a-side-card-to-centre, and an
 * optional "start on the middle card". No arrows, no dots.
 *
 * Config via attributes: `data-loop` ("true"/"false"), `data-initial` ("middle"/"first").
 * Sizing via CSS custom properties resolved per breakpoint (read from the rendered slide widths /
 * track gap, so responsive values just work).
 *
 * @extends {Component}
 */
class UgcCenterCarousel extends Component {
  // Element refs are assigned in init(); the placeholder initialisers satisfy strict property
  // initialisation without forcing null-checks at every use site.
  /** @type {HTMLElement} */ viewport = document.createElement("div");
  /** @type {HTMLElement} */ track = document.createElement("div");
  /** @type {AbortController} */ _controller = new AbortController();
  /** @type {ResizeObserver} */ _resizeObserver = new ResizeObserver(() => {});
  /** @type {Element | null} */ statusEl = null;
  /** @type {Element[]} */ realSlides = [];
  /** @type {Element[]} */ slides = [];
  /** @type {number} */ N = 0;
  /** @type {number} */ cloneCount = 0;
  /** @type {boolean} */ loop = false;
  /** @type {number} */ initialRealIndex = 0;
  /** @type {number} */ activeIndex = 0;
  /** @type {number} */ A = 0;
  /** @type {number} */ B = 0;
  /** @type {number} */ G = 0;
  /** @type {number} */ V = 0;
  /** @type {number} */ currentT = 0;
  /** @type {boolean} */ _reducedMotion = false;
  /** @type {boolean} */ _dragging = false;
  /** @type {boolean} */ _didDrag = false;
  /** @type {number} */ _dragStartX = 0;
  /** @type {number} */ _dragStartY = 0;
  /** @type {number} */ _dragStartT = 0;
  /** @type {'x' | 'y' | null} */ _axis = null;
  /** @type {number} */ _wrapTimer = 0;
  /** @type {number} */ _resizeTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Defer until idle so the component doesn't compete with critical rendering.
    requestIdleCallback(() => this.init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.teardown();
  }

  /**
   * Called by the Section Rendering API when our markup is re-rendered (e.g. a merchant edits
   * a setting in the theme editor). The server HTML only contains the real slides, so rebuild
   * from scratch — init() cleans up the previous run first.
   */
  updatedCallback() {
    super.updatedCallback();
    requestIdleCallback(() => this.init());
  }

  /** Tear down everything set up by a previous init(). Safe to call repeatedly. */
  teardown() {
    this._controller.abort();
    this._resizeObserver.disconnect();
    clearTimeout(this._wrapTimer);
    clearTimeout(this._resizeTimer);
    // The drag listeners live on window (not the AbortController) — remove them explicitly.
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    // Drop any loop clones so a re-init starts from the real slides only.
    this.track.querySelectorAll("[data-clone]").forEach((el) => el.remove());
  }

  init() {
    const viewport = /** @type {HTMLElement | null} */ (this.querySelector(".ugc-center__viewport"));
    const track = /** @type {HTMLElement | null} */ (this.querySelector(".ugc-center__track"));
    if (!viewport || !track) return;
    this.viewport = viewport;
    this.track = track;
    this.statusEl = this.querySelector(".ugc-center__status");

    // Idempotent: clear any previous run (clones/listeners/timers) before rebuilding.
    this.teardown();
    this._controller = new AbortController();

    this.realSlides = Array.from(this.track.children);
    this.N = this.realSlides.length;
    if (this.N === 0) return;

    this.loop = this.getAttribute("data-loop") === "true" && this.N > 1;
    this.initialRealIndex = this.getAttribute("data-initial") === "middle" ? Math.floor((this.N - 1) / 2) : 0;

    // Enforce the initial active on the real slides before we measure (server also marks it).
    this.realSlides.forEach((slide, i) => slide.classList.toggle("is-active", i === this.initialRealIndex));

    this.measure();

    if (this.loop) {
      this.cloneCount = this.neededCloneCount();
      this.buildClones(this.cloneCount);
    } else {
      this.cloneCount = 0;
    }

    this.slides = Array.from(this.track.children);
    this.activeIndex = this.cloneCount + this.initialRealIndex;

    this.setActive(this.activeIndex, false);
    this.setAttribute("data-ready", "");

    this.bindEvents();
  }

  /** Read the resolved metrics from the actual DOM (reliable across breakpoints). */
  measure() {
    this.V = this.viewport.clientWidth;

    const trackStyles = getComputedStyle(this.track);
    this.G = parsePx(trackStyles.columnGap || trackStyles.gap);

    const activeEl = this.querySelector(".ugc-center__slide.is-active");
    const sideEl = this.querySelectorAll(".ugc-center__slide:not(.is-active)")[0];
    this.A = activeEl ? activeEl.getBoundingClientRect().width : 360;
    this.B = sideEl ? sideEl.getBoundingClientRect().width : this.A;
    // Guard against zero/NaN (e.g. hidden while measuring).
    if (!this.A) this.A = 360;
    if (!this.B) this.B = this.A;
  }

  /** Enough clones on each side to always fill half the viewport. */
  neededCloneCount() {
    const step = this.B + this.G;
    return Math.ceil((window.innerWidth / 2 + this.A) / step) + 1;
  }

  /**
   * Prepend the last `count` real slides and append the first `count` (wrapping if count > N).
   * Clones are sanitised: no `id`s and video cards become static posters, so they never
   * double-register custom elements or duplicate IDs.
   * @param {number} count
   */
  buildClones(count) {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const lead = this.realSlides[((this.N - count + i) % this.N + this.N) % this.N];
      const trail = this.realSlides[i % this.N];
      if (lead) before.appendChild(this.makeClone(lead));
      if (trail) after.appendChild(this.makeClone(trail));
    }
    this.track.insertBefore(before, this.track.firstChild);
    this.track.appendChild(after);
  }

  /** @param {Element} node */
  makeClone(node) {
    const clone = /** @type {HTMLElement} */ (node.cloneNode(true));
    clone.classList.remove("is-active");
    clone.setAttribute("data-clone", "");
    clone.setAttribute("aria-hidden", "true");
    clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    // Replace interactive video cards with a static div that looks like the unplayed card:
    // poster image + centred play icon. Clones must never own a <video> or live controls, but
    // they must look identical to the real slide they stand in for — otherwise the side cards
    // that happen to be clones show no play CTA, and the wrap (clone → real) swap flickers.
    // Clicking a clone centres it (capture-phase onClick), so the icon needs no behaviour.
    clone.querySelectorAll("video-card-component").forEach((vc) => {
      const div = document.createElement("div");
      div.className = vc.getAttribute("class") || "";
      const posterBtn = vc.querySelector(".ugc-card__poster-btn, .video-card__poster-btn");
      const poster = vc.querySelector("img");
      const visuals = posterBtn ? Array.from(posterBtn.children) : poster ? [poster] : [];
      for (const node of visuals) {
        const copy = /** @type {HTMLElement} */ (node.cloneNode(true));
        copy.removeAttribute("id");
        copy.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
        div.appendChild(copy);
      }
      vc.replaceWith(div);
    });
    return clone;
  }

  bindEvents() {
    const { signal } = this._controller;

    // Drag / swipe. Only pointerdown is bound here; move/up are bound to `window` for the
    // duration of a drag (below). We deliberately do NOT use setPointerCapture — it suppresses
    // the follow-up `click` in some browsers, which broke click-to-centre.
    this.viewport.addEventListener("pointerdown", this.onPointerDown, { signal });
    // Don't let native image drag hijack the gesture.
    this.viewport.addEventListener("dragstart", (e) => e.preventDefault(), { signal });

    // Click a side card to centre it. Capture phase so that clicking a non-active card
    // centres it instead of triggering that card's own controls (e.g. a video's play button).
    this.track.addEventListener("click", this.onClick, { signal, capture: true });

    // Keyboard.
    this.viewport.setAttribute("tabindex", "0");
    this.viewport.addEventListener("keydown", this.onKeyDown, { signal });

    // Resize.
    this._resizeObserver = new ResizeObserver(() => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.onResize(), 150);
    });
    this._resizeObserver.observe(this.viewport);
  }

  /**
   * Position the track so the active slide is centred.
   * @param {boolean} animate
   */
  position(animate) {
    const T = this.V / 2 - this.activeIndex * (this.B + this.G) - this.A / 2;
    this.currentT = T;
    if (animate && !this._reducedMotion) {
      this.track.style.transform = `translateX(${T}px)`;
    } else {
      const prev = this.track.style.transition;
      this.track.style.transition = "none";
      this.track.style.transform = `translateX(${T}px)`;
      void this.track.offsetWidth; // flush so the next change can animate
      this.track.style.transition = prev;
    }
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  setActive(index, animate) {
    this.activeIndex = index;
    this.slides.forEach((slide, i) => slide.classList.toggle("is-active", i === this.activeIndex));
    // A/B come from measure() (init/resize), NOT from a read here — reading a slide's width
    // mid-transition returns the in-between value and would centre on the wrong width.
    this.position(animate);
    this.scheduleWrap(animate);
    this.announce();
  }

  /** Update the visually-hidden live region so screen readers hear the current slide. */
  announce() {
    const active = this.slides[this.activeIndex];
    const label = active?.getAttribute("aria-label");
    if (this.statusEl && label) this.statusEl.textContent = label;
  }

  /**
   * After a move settles, jump from a clone to its identical real slide (invisible).
   * @param {boolean} animate
   */
  scheduleWrap(animate) {
    if (!this.loop) return;
    clearTimeout(this._wrapTimer);
    const delay = animate && !this._reducedMotion ? 470 : 0;
    this._wrapTimer = setTimeout(() => this.wrap(), delay);
  }

  wrap() {
    if (!this.loop) return;
    const inCloneZone = this.activeIndex < this.cloneCount || this.activeIndex >= this.cloneCount + this.N;
    if (!inCloneZone) return;
    const realIndex = ((this.activeIndex - this.cloneCount) % this.N + this.N) % this.N;
    this.activeIndex = this.cloneCount + realIndex;
    // Swap clone → identical real slide with ALL transitions off, so the width/position change
    // is instantaneous and invisible (no second grow/shrink "ripple").
    this.classList.add("is-jumping");
    this.slides.forEach((slide, i) => slide.classList.toggle("is-active", i === this.activeIndex));
    this.position(false);
    void this.track.offsetWidth; // commit the no-transition state before re-enabling
    this.classList.remove("is-jumping");
  }

  /** @param {number} delta */
  go(delta) {
    this.setActive(this.activeIndex + delta, true);
  }

  // --- Pointer drag ---------------------------------------------------------

  /** @param {PointerEvent} e */
  onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    this._dragging = true;
    this._didDrag = false;
    this._axis = null; // undecided until the first meaningful move (see onPointerMove)
    this._dragStartX = e.clientX;
    this._dragStartY = e.clientY;
    this._dragStartT = this.currentT;
    // Track the gesture on window so it keeps working if the pointer leaves the viewport.
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
  };

  /** @param {PointerEvent} e */
  onPointerMove = (e) => {
    if (!this._dragging) return;
    const dx = e.clientX - this._dragStartX;
    const dy = e.clientY - this._dragStartY;

    // Lock the axis on the first ~8px of movement. If the gesture is vertical, abandon it so
    // the page scrolls normally — never move the track for a vertical (scroll) gesture. The
    // ~8px slop also means a slightly shaky tap still registers as a click, not a drag.
    if (this._axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        this._axis = "y";
        this.endDrag(); // vertical → hand the gesture back to the browser
        return;
      }
      this._axis = "x";
      this._didDrag = true;
      this.track.style.transition = "none";
    }

    if (this._axis !== "x") return;
    this.track.style.transform = `translateX(${this._dragStartT + dx}px)`;
  };

  /** @param {PointerEvent} e */
  onPointerUp = (e) => {
    if (!this._dragging) return;
    const wasHorizontal = this._axis === "x";
    this.endDrag();
    if (!wasHorizontal) return; // vertical gesture or a tap — nothing to settle
    this.track.style.transition = "";
    const dx = e.clientX - this._dragStartX;
    let step = Math.round(-dx / (this.B + this.G));
    if (step === 0 && Math.abs(dx) > 40) step = dx < 0 ? 1 : -1;
    if (step !== 0) this.setActive(this.activeIndex + step, true);
    else this.position(true); // snap back
  };

  /** Stop tracking the current gesture and detach the window listeners. */
  endDrag() {
    this._dragging = false;
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
  }

  // --- Click / keyboard -----------------------------------------------------

  /** @param {MouseEvent} e */
  onClick = (e) => {
    const slide = e.target instanceof Element ? e.target.closest(".ugc-center__slide") : null;
    if (!slide) return;

    // A click that ends a drag shouldn't do anything (and shouldn't reach card controls).
    if (this._didDrag) {
      this._didDrag = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const idx = this.slides.indexOf(slide);
    if (idx === -1 || idx === this.activeIndex) return; // active card: let its own controls handle the click

    // Centre the clicked (non-active) card. Note the card's own `on:click` handlers have ALREADY
    // run by now — the theme delegates them on `document` in the capture phase, ahead of this
    // track listener — so a real video side card starts playing as it centres. Stop the click
    // here anyway so nothing further down the tree reacts to it.
    e.preventDefault();
    e.stopPropagation();
    this.setActive(idx, true);

    // A clone is a static poster (see makeClone) with no `on:click` to fire, so it would centre
    // without playing. Start the real slide it stands in for, so a clone click behaves exactly
    // like a real side-card click once wrap() swaps the two.
    if (slide.hasAttribute("data-clone")) this.playRealSlide(idx);
  };

  /**
   * Reveal + play the deferred video of the real slide that the given (clone) index maps to.
   * Image-only cards have no <deferred-media>, so this is a no-op for them.
   * @param {number} idx
   */
  playRealSlide(idx) {
    const realIndex = (((idx - this.cloneCount) % this.N) + this.N) % this.N;
    const media = /** @type {any} */ (this.realSlides[realIndex]?.querySelector("deferred-media"));
    if (!media) return;

    if (media.getAttribute("data-media-loaded")) {
      // Already revealed once (e.g. played on a previous pass round the loop, then paused when
      // another card started). showDeferredMedia() only plays on the *first* reveal — calling
      // it again would flip the icon to "playing" without touching the video — so resume
      // through playMedia() instead.
      if (!media.isPlaying && typeof media.playMedia === "function") media.playMedia();
    } else if (typeof media.showDeferredMedia === "function") {
      media.showDeferredMedia();
    }
  }

  /** @param {KeyboardEvent} e */
  onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      this.go(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.go(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      this.setActive(this.cloneCount, true); // first real slide
    } else if (e.key === "End") {
      e.preventDefault();
      this.setActive(this.cloneCount + this.N - 1, true); // last real slide
    }
  };

  // --- Resize ---------------------------------------------------------------

  onResize() {
    const realIndex = this.loop
      ? ((this.activeIndex - this.cloneCount) % this.N + this.N) % this.N
      : this.activeIndex;
    this.measure();

    if (this.loop) {
      const needed = this.neededCloneCount();
      if (needed !== this.cloneCount) {
        this.track.querySelectorAll("[data-clone]").forEach((el) => el.remove());
        this.cloneCount = needed;
        this.buildClones(needed);
        this.slides = Array.from(this.track.children);
      }
      this.activeIndex = this.cloneCount + realIndex;
    }

    this.slides.forEach((slide, i) => slide.classList.toggle("is-active", i === this.activeIndex));
    this.position(false);
  }
}

/** @param {string} value */
function parsePx(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

if (!customElements.get("ugc-center-carousel")) {
  customElements.define("ugc-center-carousel", UgcCenterCarousel);
}
